import React, { useContext, useMemo, useState } from 'react';
import { Button, Modal } from 'antd';

import AmazonWebServicesContext from '@totem/components/administration/amazonWebServices/AmazonWebServicesContext';
import { AwsInstanceRecord } from '@totem/components/administration/amazonWebServices/types';
import GenericAutoComplete from '@totem/components/common/autoCompletes/GenericAutoComplete';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AutoCompleteValue } from '@totem/types/autoComplete';
import { Filter } from '@totem/types/common';
import { AWS_INSTANCES_ENDPOINT } from '@totem/utilities/endpoints';

const EMPTY_VALUE: AutoCompleteValue = { value: EMPTY_ID, display: '' };

type EditorProps = {
  instance: AwsInstanceRecord;
  onClose: () => void;
  onSaved: () => void;
};

// Split from the gate below so state initializes from the instance the
// modal was opened for, rather than whatever was selected at first mount.
const AssignDeviceEditor = ({ instance, onClose, onSaved }: EditorProps) => {
  const { getApiToken } = useContext(UserProfileContext);
  const currentDevice = instance.item.device;
  const hasCurrentDevice =
    Boolean(currentDevice?.id) && currentDevice.id !== EMPTY_ID;
  const [selectedDevice, setSelectedDevice] = useState<AutoCompleteValue>(
    hasCurrentDevice
      ? { value: currentDevice.id, display: currentDevice.name }
      : EMPTY_VALUE,
  );
  const [saving, setSaving] = useState<boolean>(false);

  // Narrow the search to the instance's building once a hierarchy has been
  // assigned; otherwise search every device in the current organization.
  const buildingId = instance.hierarchyReference?.building?.id;
  const filters = useMemo<Filter[]>(
    () =>
      buildingId && buildingId !== EMPTY_ID
        ? [{ field: 'buildingId', values: [buildingId] }]
        : [],
    [buildingId],
  );

  const saveDevice = (device: AutoCompleteValue) => {
    setSaving(true);

    fetch(
      `${AWS_INSTANCES_ENDPOINT}/${instance.item.instance.instanceId}/device`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getApiToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: device.value, name: device.display }),
      },
    ).then(() => {
      setSaving(false);

      onClose();

      onSaved();
    });
  };

  const hasSelection =
    selectedDevice.value !== EMPTY_ID && selectedDevice.value !== '';

  return (
    <Modal
      open
      title={<ModalTitle>Assign Device</ModalTitle>}
      onCancel={onClose}
      footer={
        <ModalFooter>
          {hasCurrentDevice && (
            <Button
              danger
              onClick={() => saveDevice(EMPTY_VALUE)}
              disabled={saving}
            >
              Clear Device
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => saveDevice(selectedDevice)}
            loading={saving}
            disabled={!hasSelection}
          >
            Save
          </Button>
        </ModalFooter>
      }
    >
      <p>
        Instance: <strong>{instance.name}</strong>
      </p>
      <GenericAutoComplete
        type="Device"
        label="Search devices"
        limit={25}
        filters={filters}
        selectedValue={selectedDevice}
        onSelect={setSelectedDevice}
        onChange={() => {}}
      />
    </Modal>
  );
};

const AssignDeviceModal = () => {
  const { selectedInstance, activeAction, onActionClose, onRefresh } =
    useContext(AmazonWebServicesContext);

  if (activeAction !== 'device' || !selectedInstance) {
    return null;
  }

  return (
    <AssignDeviceEditor
      key={selectedInstance.id}
      instance={selectedInstance}
      onClose={onActionClose}
      onSaved={onRefresh}
    />
  );
};

export default AssignDeviceModal;
